import React from "react";
import { apiFetch } from "../lib/api";

const DEFAULT_TEMPLATE_NAME = "Default Outreach";
const DEFAULT_TEMPLATE_SUBJECT = "Feature request for {{playlist_name}}";
const DEFAULT_TEMPLATE_HTML = `<p>Hi {{curator_name}},</p><p>I came across your playlist <a href="{{playlist_url}}">{{playlist_name}}</a> and thought this release could be a great fit.</p><p>Best,<br>{{sender_name}}</p>`;
const DEFAULT_TEMPLATE_TEXT = `Hi {{curator_name}}, I came across your playlist {{playlist_name}} ({{playlist_url}}) and thought this release could be a great fit. Best, {{sender_name}}`;

async function apiCreateTemplate({ name, subject, html, text = null }) {
  const j = await apiFetch("/api/email/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, subject, html, text }),
  });
  return j?.id;
}

async function apiUpdateTemplate(id, { name, subject, html, text = null }) {
  return apiFetch(`/api/email/templates/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, subject, html, text }),
  });
}

function TemplateEditor({ templates, tplLoading, tplErr, onRefresh, onSaved, sampleRow }) {
  const [tplName, setTplName] = React.useState("");
  const [tplSubject, setTplSubject] = React.useState("");
  const [tplHtml, setTplHtml] = React.useState("");
  const [tplText, setTplText] = React.useState("");
  const [editingTplId, setEditingTplId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const subjectRef = React.useRef(null);
  const htmlRef = React.useRef(null);
  const textRef = React.useRef(null);

  const TOKENS = [
    { key: "playlist_name", label: "Playlist name" },
    { key: "playlist_url", label: "Playlist URL" },
    { key: "curator_name", label: "Curator name" },
    { key: "recipient_email", label: "Recipient email" },
    { key: "sender_name", label: "Sender name" },
  ];

  const insertToken = (ref, value, setter, tokenKey) => {
    const token = `{{${tokenKey}}}`;
    const el = ref.current;
    const val = value || "";
    if (!el) {
      setter(val + token);
      return;
    }
    const s = el.selectionStart ?? val.length;
    const e = el.selectionEnd ?? s;
    const next = val.slice(0, s) + token + val.slice(e);
    setter(next);
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus();
        ref.current.selectionStart = ref.current.selectionEnd = s + token.length;
      }
    });
  };

  const loadDefault = () => {
    setTplName(DEFAULT_TEMPLATE_NAME);
    setTplSubject(DEFAULT_TEMPLATE_SUBJECT);
    setTplHtml(DEFAULT_TEMPLATE_HTML);
    setTplText(DEFAULT_TEMPLATE_TEXT);
    setEditingTplId(null);
  };

  const newBlank = () => {
    setTplName("");
    setTplSubject("");
    setTplHtml("");
    setTplText("");
    setEditingTplId(null);
  };

  const save = async () => {
    if (!tplName.trim() || !tplSubject.trim() || !tplHtml.trim()) return;
    setSaving(true);
    try {
      if (editingTplId) {
        await apiUpdateTemplate(editingTplId, {
          name: tplName.trim(),
          subject: tplSubject,
          html: tplHtml,
          text: tplText || null,
        });
      } else {
        const id = await apiCreateTemplate({
          name: tplName.trim(),
          subject: tplSubject,
          html: tplHtml,
          text: tplText || null,
        });
        setEditingTplId(id || null);
      }
      await onRefresh();
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  };

  const sample = sampleRow || {
    name: "Playlist Name",
    url: "https://open.spotify.com/playlist/xyz",
    curator: "Curator",
    email: "curator@example.com",
  };
  const map = {
    playlist_name: sample.name || "",
    playlist_url: sample.url || "",
    curator_name: sample.curator || "",
    recipient_email: sample.email || "",
    sender_name: "",
  };
  const applyTokens = (s) => (s || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => map[k] ?? "");

  return (
    <div>
      <h4 style={{ margin: "0 0 8px" }}>Templates</h4>

      <div style={{ display: "grid", gap: 8 }}>
        <input className="psl-input" placeholder="Template name" value={tplName} onChange={(e) => setTplName(e.target.value)} />

        <div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "var(--psl-muted)" }}>Subject tokens:</span>
            {TOKENS.map((t) => (
              <button key={"s_" + t.key} className="psl-pill" onClick={() => insertToken(subjectRef, tplSubject, setTplSubject, t.key)}>
                {t.label}
              </button>
            ))}
          </div>
          <input ref={subjectRef} className="psl-input" placeholder="Subject" value={tplSubject} onChange={(e) => setTplSubject(e.target.value)} />
        </div>

        <div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "var(--psl-muted)" }}>HTML tokens:</span>
            {TOKENS.map((t) => (
              <button key={"h_" + t.key} className="psl-pill" onClick={() => insertToken(htmlRef, tplHtml, setTplHtml, t.key)}>
                {t.label}
              </button>
            ))}
          </div>
          <textarea ref={htmlRef} className="psl-input" style={{ minHeight: 140 }} placeholder="HTML body" value={tplHtml} onChange={(e) => setTplHtml(e.target.value)} />
        </div>

        <div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: "var(--psl-muted)" }}>Plain-text tokens:</span>
            {TOKENS.map((t) => (
              <button key={"t_" + t.key} className="psl-pill" onClick={() => insertToken(textRef, tplText, setTplText, t.key)}>
                {t.label}
              </button>
            ))}
          </div>
          <textarea ref={textRef} className="psl-input" style={{ minHeight: 80 }} placeholder="Plain text (optional)" value={tplText} onChange={(e) => setTplText(e.target.value)} />
        </div>

        <div className="psl-actions" style={{ display: "flex", gap: 8 }}>
          <button className="psl-btn" onClick={loadDefault}>New From Default</button>
          <button className="psl-btn" onClick={newBlank}>New Blank</button>
          <button className="psl-btn primary" onClick={save} disabled={saving}>{saving ? "Saving…" : editingTplId ? "Update Template" : "Save Template"}</button>
          <button className="psl-btn" onClick={onRefresh} disabled={tplLoading}>Refresh</button>
        </div>
      </div>

      <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
        <div style={{ border: "1px dashed var(--psl-border)", borderRadius: 8, overflow: "auto" }}>
          {tplLoading ? (
            <div style={{ padding: 8, color: "var(--psl-muted)" }}>Loading…</div>
          ) : tplErr ? (
            <div style={{ padding: 8, color: "#9b1c1c" }}>{tplErr}</div>
          ) : templates.length === 0 ? (
            <div style={{ padding: 8, color: "var(--psl-muted)" }}>No templates yet.</div>
          ) : (
            <table className="psl-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subject</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>{t.subject}</td>
                    <td>
                      <button
                        className="psl-btn"
                        onClick={() => {
                          setEditingTplId(t.id);
                          setTplName(t.name || "");
                          setTplSubject(t.subject || "");
                          setTplHtml(t.html || "");
                          setTplText(t.text || "");
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="psl-btn"
                        onClick={async () => {
                          await apiUpdateTemplate(t.id, {
                            name: t.name,
                            subject: t.subject,
                            html: t.html,
                            text: t.text || null,
                          });
                          await onRefresh();
                        }}
                      >
                        Save As Is
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ border: "1px solid var(--psl-border)", borderRadius: 8, padding: 10, background: "#fff" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Preview</div>
          <div style={{ marginBottom: 8 }}><b>Subject:</b> {applyTokens(tplSubject)}</div>
          <div dangerouslySetInnerHTML={{ __html: applyTokens(tplHtml) }} />
          {tplText ? (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--psl-border)" }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Plain Text</div>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{applyTokens(tplText)}</pre>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default TemplateEditor;
